import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    SelectChangeEvent,
    IconButton,
    Tooltip,
    Alert
} from '@mui/material';
import {
    FilterList as FilterIcon,
    Clear as ClearIcon,
    Search as SearchIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { PrescriptionFilter } from '../../services/prescriptionService';

interface RecupFiltersDialogProps {
    open: boolean;
    onClose: () => void;
    filters: PrescriptionFilter;
    onFiltersChange: (filters: PrescriptionFilter) => void;
    loading?: boolean;
}

const RecupFiltersDialog: React.FC<RecupFiltersDialogProps> = ({
    open,
    onClose,
    filters,
    onFiltersChange,
    loading = false
}) => {
    const [localFilters, setLocalFilters] = useState<PrescriptionFilter>(filters);
    const [dateFrom, setDateFrom] = useState<Date | null>(
        filters.dateFrom ? new Date(filters.dateFrom) : null
    );
    const [dateTo, setDateTo] = useState<Date | null>(
        filters.dateTo ? new Date(filters.dateTo) : null
    );
    // Nuovi campi per Data Accettazione
    const [acceptanceDateFrom, setAcceptanceDateFrom] = useState<Date | null>(null);
    const [acceptanceDateTo, setAcceptanceDateTo] = useState<Date | null>(null);

    // Update local state when external filters change
    useEffect(() => {
        setLocalFilters(filters);
        setDateFrom(filters.dateFrom ? new Date(filters.dateFrom) : null);
        setDateTo(filters.dateTo ? new Date(filters.dateTo) : null);
    }, [filters]);

    const handleInputChange = (field: keyof PrescriptionFilter, value: any) => {
        const updatedFilters = {
            ...localFilters,
            [field]: value || undefined
        };
        setLocalFilters(updatedFilters);
    };

    const handleDateFromChange = (date: Date | null) => {
        setDateFrom(date);
        const dateString = date ? date.toISOString().split('T')[0] : undefined;
        handleInputChange('dateFrom', dateString);
    };

    const handleDateToChange = (date: Date | null) => {
        setDateTo(date);
        const dateString = date ? date.toISOString().split('T')[0] : undefined;
        handleInputChange('dateTo', dateString);
    };

    const handleAcceptanceDateFromChange = (date: Date | null) => {
        setAcceptanceDateFrom(date);
        // TODO: Aggiungere questi campi al tipo PrescriptionFilter quando implementati nel backend
        // const dateString = date ? date.toISOString().split('T')[0] : undefined;
        // handleInputChange('acceptanceDateFrom', dateString);
    };

    const handleAcceptanceDateToChange = (date: Date | null) => {
        setAcceptanceDateTo(date);
        // TODO: Aggiungere questi campi al tipo PrescriptionFilter quando implementati nel backend
        // const dateString = date ? date.toISOString().split('T')[0] : undefined;
        // handleInputChange('acceptanceDateTo', dateString);
    };

    const handleSyncStatusChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        handleInputChange('syncStatus', value || undefined);
    };

    const handleHealthNetSyncedChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        let booleanValue: boolean | undefined = undefined;
        if (value === 'true') booleanValue = true;
        if (value === 'false') booleanValue = false;
        handleInputChange('healthnetSynced', booleanValue);
    };

    const handleApplyFilters = () => {
        // Applica i filtri e chiude il dialog
        onFiltersChange(localFilters);
        onClose();
    };

    const handleClear = () => {
        const clearedFilters: PrescriptionFilter = {
            page: 1,
            limit: filters.limit || 50
        };
        setLocalFilters(clearedFilters);
        setDateFrom(null);
        setDateTo(null);
        setAcceptanceDateFrom(null);
        setAcceptanceDateTo(null);
    };

    const handleClose = () => {
        // Reset ai filtri originali se non sono stati applicati
        setLocalFilters(filters);
        setDateFrom(filters.dateFrom ? new Date(filters.dateFrom) : null);
        setDateTo(filters.dateTo ? new Date(filters.dateTo) : null);
        setAcceptanceDateFrom(null);
        setAcceptanceDateTo(null);
        onClose();
    };

    const getActiveFiltersCount = (): number => {
        let count = 0;
        if (localFilters.nre) count++;
        if (localFilters.patientName) count++;
        if (localFilters.patientFiscalCode) count++;
        if (localFilters.dateFrom) count++;
        if (localFilters.dateTo) count++;
        if (localFilters.syncStatus) count++;
        if (localFilters.healthnetSynced !== undefined) count++;
        if (acceptanceDateFrom) count++; // TODO: quando implementato nel backend
        if (acceptanceDateTo) count++; // TODO: quando implementato nel backend
        return count;
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleApplyFilters();
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { minHeight: 600 }
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterIcon />
                        Filtri Sincronizzazione ReCup
                    </Box>
                    <IconButton onClick={handleClose} disabled={loading}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Box sx={{ mb: 3 }}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2">
                                <strong>Configurazione filtri per la sincronizzazione con ReCup</strong><br/>
                                Imposta i criteri di ricerca per sincronizzare le prescrizioni dal sistema RECUP. 
                                I filtri verranno applicati alla ricerca successiva.
                            </Typography>
                        </Alert>

                        {getActiveFiltersCount() > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Chip 
                                    label={`${getActiveFiltersCount()} filtri attivi`} 
                                    color="primary" 
                                    variant="outlined"
                                    size="medium"
                                />
                            </Box>
                        )}
                    </Box>

                    <Grid container spacing={3}>
                        {/* Primary Search Fields */}
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="NRE (Numero Ricetta)"
                                value={localFilters.nre || ''}
                                onChange={(e) => handleInputChange('nre', e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Inserisci NRE..."
                                variant="outlined"
                                size="medium"
                                disabled={loading}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Nome Assistito"
                                value={localFilters.patientName || ''}
                                onChange={(e) => handleInputChange('patientName', e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Nome e/o cognome..."
                                variant="outlined"
                                size="medium"
                                disabled={loading}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Codice Fiscale Assistito"
                                value={localFilters.patientFiscalCode || ''}
                                onChange={(e) => handleInputChange('patientFiscalCode', e.target.value.toUpperCase())}
                                onKeyPress={handleKeyPress}
                                placeholder="RSSMRA80A01H501Z"
                                variant="outlined"
                                size="medium"
                                disabled={loading}
                                inputProps={{ maxLength: 16 }}
                            />
                        </Grid>

                        {/* Date Range Fields - Prescrizione */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                                Date Prescrizione
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <DatePicker
                                label="Data Prescrizione Da"
                                value={dateFrom}
                                onChange={handleDateFromChange}
                                disabled={loading}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        variant: 'outlined',
                                        size: 'medium'
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <DatePicker
                                label="Data Prescrizione A"
                                value={dateTo}
                                onChange={handleDateToChange}
                                disabled={loading}
                                minDate={dateFrom || undefined}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        variant: 'outlined',
                                        size: 'medium'
                                    }
                                }}
                            />
                        </Grid>

                        {/* Date Range Fields - Accettazione */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                                Date Accettazione
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <DatePicker
                                label="Data Accettazione Da"
                                value={acceptanceDateFrom}
                                onChange={handleAcceptanceDateFromChange}
                                disabled={loading}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        variant: 'outlined',
                                        size: 'medium'
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <DatePicker
                                label="Data Accettazione A"
                                value={acceptanceDateTo}
                                onChange={handleAcceptanceDateToChange}
                                disabled={loading}
                                minDate={acceptanceDateFrom || undefined}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        variant: 'outlined',
                                        size: 'medium'
                                    }
                                }}
                            />
                        </Grid>

                        {/* Status Filters */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                                Stato Sincronizzazione
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth variant="outlined" size="medium">
                                <InputLabel>Stato Sincronizzazione RECUP</InputLabel>
                                <Select
                                    value={localFilters.syncStatus || ''}
                                    onChange={handleSyncStatusChange}
                                    input={<OutlinedInput label="Stato Sincronizzazione RECUP" />}
                                    disabled={loading}
                                >
                                    <MenuItem value="">
                                        <em>Tutti</em>
                                    </MenuItem>
                                    <MenuItem value="pending">In Attesa</MenuItem>
                                    <MenuItem value="synced">Sincronizzata</MenuItem>
                                    <MenuItem value="failed">Fallita</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth variant="outlined" size="medium">
                                <InputLabel>Stato HealthNET</InputLabel>
                                <Select
                                    value={localFilters.healthnetSynced !== undefined ? localFilters.healthnetSynced.toString() : ''}
                                    onChange={handleHealthNetSyncedChange}
                                    input={<OutlinedInput label="Stato HealthNET" />}
                                    disabled={loading}
                                >
                                    <MenuItem value="">
                                        <em>Tutti</em>
                                    </MenuItem>
                                    <MenuItem value="true">Sincronizzata</MenuItem>
                                    <MenuItem value="false">Non Sincronizzata</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                        <Tooltip title="Cancella tutti i filtri">
                            <IconButton
                                onClick={handleClear}
                                disabled={loading || getActiveFiltersCount() === 0}
                                color="default"
                            >
                                <ClearIcon />
                            </IconButton>
                        </Tooltip>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button onClick={handleClose} disabled={loading}>
                                Annulla
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleApplyFilters}
                                disabled={loading}
                                startIcon={<SearchIcon />}
                                size="large"
                            >
                                Applica Filtri
                            </Button>
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default RecupFiltersDialog;